import React, { useRef, useCallback } from 'react';
import { User, MapPin, Search } from 'lucide-react';

/**
 * Tab navigation component for mobile panel switching
 * Displays at top of mobile view to switch between Character, Scene, and Clues panels
 */

const defaultTabs = [
  { id: 'character', label: 'Character', icon: User },
  { id: 'scene', label: 'Scene', icon: MapPin },
  { id: 'clues', label: 'Clues', icon: Search }
];

export function MobilePanelTabs({
  activePanel,
  setActivePanel,
  tabs = defaultTabs,
  className = '',
  ariaLabel = 'Panel navigation'
}) {
  const tabRefs = useRef({});

  const handleKeyDown = useCallback((e, currentIndex) => {
    const tabIds = tabs.map(t => t.id);
    let newIndex = currentIndex;

    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        newIndex = currentIndex === 0 ? tabIds.length - 1 : currentIndex - 1;
        break;
      case 'ArrowRight':
        e.preventDefault();
        newIndex = currentIndex === tabIds.length - 1 ? 0 : currentIndex + 1;
        break;
      case 'Home':
        e.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        newIndex = tabIds.length - 1;
        break;
      default:
        return;
    }

    setActivePanel(tabIds[newIndex]);
    tabRefs.current[tabIds[newIndex]]?.focus();
  }, [tabs, setActivePanel]);

  return (
    <div
      className={`flex w-full bg-black/40 border-b border-white/10 ${className}`}
      role="tablist"
      aria-label={ariaLabel}
    >
      {tabs.map((tab, index) => {
        const Icon = tab.icon;
        const isActive = activePanel === tab.id;

        return (
          <button
            key={tab.id}
            ref={el => tabRefs.current[tab.id] = el}
            id={`tab-${tab.id}`}
            onClick={() => setActivePanel(tab.id)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className={`
              flex-1 flex items-center justify-center gap-2
              min-h-touch py-3 px-2
              text-sm font-medium
              transition-colors duration-200
              ${isActive
                ? 'text-white bg-white/10 border-b-2 border-amber-500'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
              }
            `}
            aria-selected={isActive}
            aria-controls={`panel-${tab.id}`}
            tabIndex={isActive ? 0 : -1}
            role="tab"
          >
            <Icon className="w-4 h-4" />
            <span>{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export default MobilePanelTabs;
